/**
 * REAL Canva folder map for the SR Artwork Engine.
 *
 * These are the VERIFIED folder ids in the connected SR Canva account. The
 * element pickers open the folder that matches the input being overridden and
 * let the rep drill into its subfolders (e.g. Side Shanks → Football → the many
 * football shank designs), breadcrumb back, and pick a design.
 *
 * Only ids Brian confirmed are hard-coded. Anything not yet confirmed resolves
 * to the engine ROOT so the rep can always navigate to it by hand rather than
 * hitting a dead picker — and the moment a folder is confirmed it drops in here
 * with no other code change.
 *
 * BRIAN-SIDE DEPENDENCY (tracked, not blocking): the sport SUBFOLDERS under
 * Side Shanks / Bezel & Top, and a Backgrounds folder of brand templates with
 * color slots. The navigator renders whatever structure exists today; deeper
 * organization on the Canva side immediately makes the drill-down richer.
 */

import { DESIGN_LIBRARY_NAMES } from '../studio/designLibrary.js'

/** Verified folder ids. */
export const CANVA_FOLDERS = {
  root: { id: 'FAHQEPDVjdI', name: 'SR Artwork Engine', verified: true },
  bezel: { id: 'FAHQEAIMN7g', name: 'Bezel & Top Designs', verified: true },
  shanks: { id: 'FAHQEEVdZ8M', name: 'Side Shanks', verified: true },
  // Not yet confirmed — resolve to root so the rep navigates there manually.
  backgrounds: { id: 'FAHQEPDVjdI', name: 'SR Artwork Engine', verified: false },
  crests: { id: 'FAHQEPDVjdI', name: 'SR Artwork Engine', verified: false },
}

export const CANVA_ROOT_ID = CANVA_FOLDERS.root.id

/**
 * Every folder id we know a name for — used to seed the navigator breadcrumb.
 *
 * The design-library names come from ONE place (`studio/designLibrary.js`), the
 * same module `studio/folders.js` reads, so the two navigators cannot drift on
 * them. That drift is exactly what broke drill-in on the live site once
 * already (CLAUDE.md, "A FOLDER IS A FOLDER"). Naming only — `folderForInput`
 * below still never routes an element input into the design library.
 */
export const FOLDER_NAMES = Object.fromEntries([
  ...Object.entries(DESIGN_LIBRARY_NAMES),
  ...Object.values(CANVA_FOLDERS).map((f) => [f.id, f.name]),
])

/**
 * Which Canva folder an element input opens on.
 * Keyed by ZONE id (bezel/top, shank-left, shank-right) or input key
 * (crest, background). Unknown inputs open the engine root.
 */
export function folderForInput(key = '') {
  const k = String(key).toLowerCase()
  if (/bezel|top|face|head|crown/.test(k)) return CANVA_FOLDERS.bezel
  if (/shank|band|edge|border/.test(k)) return CANVA_FOLDERS.shanks
  if (/background|backdrop|composition/.test(k)) return CANVA_FOLDERS.backgrounds
  if (/crest|logo|mark|mascot/.test(k)) return CANVA_FOLDERS.crests
  return CANVA_FOLDERS.root
}
